/**
 * Legacy schema-avatar CSS class → Iconify (lucide) name map.
 *
 * Before the unified glyph system, schema types stored a CSS class string
 * (e.g. 'center-organization-icon') resolved against bespoke SVG-in-SCSS rules.
 * Those SCSS classes have been deleted, and `schema_versions.avatar` was
 * backfilled to Iconify names. However, legacy class strings still surface at
 * runtime from non-schema sources that were never backfilled (metasphere
 * identity avatars, hardcoded fallbacks). Without this normalizer they would hit
 * GlyphAtom's letter branch and render as a stray 'C'.
 *
 * Mirrors db/migrations/helpers/avatarGlyphMap.js (the backfill source of truth).
 * Keep the two in sync when adding entries.
 */
const LEGACY_AVATAR_TO_GLYPH: Record<string, string> = {
  // Structural types
  'center-organization-icon': 'lucide:globe',
  'center-department-icon': 'lucide:box',
  'center-group-icon': 'lucide:users',
  'center-sphere-icon': 'lucide:circle-dot',
  // Subject types
  'center-subject-icon': 'lucide:user',
  'center-subject-identity-icon': 'lucide:contact',
  'center-subject-user-icon': 'lucide:user',
  'center-subject-role-icon': 'lucide:shield',
  'sphere-user-icon': 'lucide:user',
  'sphere-role-icon': 'lucide:shield',
  // Work-item types
  'center-perspective-icon': 'lucide:eye',
  'center-time-action-icon': 'lucide:diamond',
  'center-orbit-icon': 'lucide:refresh-cw',
  'center-rotation-icon': 'lucide:rotate-cw',
  // Strategic types
  'center-sphere-priority-icon': 'lucide:target',
  'center-sphere-pillar-icon': 'lucide:layers',
  'center-sphere-strategy-icon': 'lucide:git-branch',
  'center-sphere-intention-icon': 'lucide:lightbulb',
  // Calendar types
  'center-calendar-period-icon': 'lucide:calendar-days',
  'center-calendar-timeframe-icon': 'lucide:calendar-range',
};

/**
 * Resolves a legacy avatar CSS class to its Iconify name. Any value that is not
 * a known legacy class (already-Iconify names, letters, null) is returned as-is.
 */
export function normalizeLegacyGlyph(
  glyph: string | null | undefined
): string | null | undefined {
  if (!glyph) return glyph;
  return LEGACY_AVATAR_TO_GLYPH[glyph] ?? glyph;
}

export { LEGACY_AVATAR_TO_GLYPH };
